import { useState, useMemo } from "react";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { useListExports, useCreateExport, useDeleteExport, getListExportsQueryKey, usePreviewExport, usePreviewLessonsExport, type ExportInputType } from "@workspace/api-client-react";
import { Link } from "wouter";
import { Download, Trash2, Plus, FileSpreadsheet, FileText, Info, ExternalLink, Building2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import { useRegion } from "@/contexts/RegionContext";

export default function Exports() {
  const { getToken } = useAuth();
  const { data: exports, isLoading } = useListExports();
  const createExport = useCreateExport();
  const deleteExport = useDeleteExport();
  const { toast } = useToast();
  const { terms, region } = useRegion();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<ExportInputType>("csv");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Phase 2 (Task #56) — only meaningful when type === "csv". Default
  // "current" means: use the account default display mode (and any
  // per-student overrides) — the file looks the same as the rest of the app.
  const [identifierMode, setIdentifierMode] = useState<"current" | "code" | "sequential">("current");
  const [downloading, setDownloading] = useState<number | null>(null);

  const isAuRegion = region === "au";

  const complianceSystem = terms.complianceSystem;
  const complianceBadge  = isAuRegion ? "Sentral CSV" : "Compliance CSV";

  const CSV_COLUMNS = [
    "Date", "Period", "Record Type",
    "Course", "Class", "Subject",
    "Students Involved", "Quick Notes", "Formal Description",
    terms.exportAdjustmentColHeader, terms.uploadedToSystem, "Created At",
  ];

  const COMPLIANCE_LOG_COLUMNS = [
    "Student Last Name", "Student First Name", `${terms.yearLevelPrefix} Level`, "Class",
    "Date", "Period", "Subject",
    "Adjustment Categories", terms.exportAdjustmentColHeader,
    "Evidence Note", "Quick Notes", terms.uploadedToSystem,
  ];

  const COMPLIANCE_SUMMARY_COLUMNS = [
    "Student Last Name", "Student First Name", `${terms.yearLevelPrefix} Level`, "Class(es)",
    `Total ${terms.adjustmentShort} Records`, "First Recorded", "Most Recent",
    "Unique Adjustments", "All Adjustments", "Most Recent Evidence Note",
  ];

  const EXPORT_TYPES = useMemo(() => [
    {
      value: "csv",
      label: "All Records — CSV",
      description: `Every student record (${terms.adjustmentShort}, contact, ${terms.behaviourSpelling.toLowerCase()}, general) in one file. Opens in Excel.`,
      icon: FileSpreadsheet,
      badge: "CSV",
      columns: CSV_COLUMNS,
      hint: "Date, period, course, class and subject are captured automatically from the diary.",
      compliance: false,
    },
    {
      value: "lessons_pdf",
      label: "Lesson Plans — HTML (print to PDF)",
      description: "All lesson plans with rich text and embedded images. Open in your browser, then Ctrl+P → Save as PDF.",
      icon: FileText,
      badge: "HTML→PDF",
      columns: null as string[] | null,
      hint: "Images you've pasted into period lesson plans are embedded directly in the file.",
      compliance: false,
    },
    {
      value: "sentral_nccd_log",
      label: isAuRegion
        ? "Sentral — NCCD Evidence Log"
        : `${complianceSystem} — Evidence Log`,
      description: isAuRegion
        ? "One row per student per NCCD observation. Use this as your ongoing evidence log when entering adjustments into Sentral throughout the year."
        : `One row per student per ${terms.adjustmentShort} observation. Use as your ongoing evidence log when uploading to your compliance system.`,
      icon: Building2,
      badge: complianceBadge,
      columns: COMPLIANCE_LOG_COLUMNS,
      hint: isAuRegion
        ? "NCCD records only. Each row is one observation for one student — ideal for copy-pasting notes into Sentral's Disability & Inclusion module."
        : `${terms.adjustmentType} records only. One row per student per observation.`,
      compliance: true,
    },
    {
      value: "sentral_nccd_summary",
      label: isAuRegion
        ? "Sentral — NCCD Annual Summary"
        : `${complianceSystem} — Annual Summary`,
      description: isAuRegion
        ? "One row per student with all adjustments and evidence aggregated. Use this at NCCD collection time to enter each student's data into Sentral."
        : `One row per student with all ${terms.adjustmentShort} data aggregated. Use this at compliance reporting time.`,
      icon: Building2,
      badge: complianceBadge,
      columns: COMPLIANCE_SUMMARY_COLUMNS,
      hint: isAuRegion
        ? "NCCD records only. Students sorted alphabetically. Set the Level of Adjustment in Sentral manually based on the evidence provided."
        : `${terms.adjustmentType} records only. Students sorted alphabetically.`,
      compliance: true,
    },
  ], [terms, region]);

  const handleOpen = () => {
    setName("");
    setType("csv" as ExportInputType);
    setDateFrom("");
    setDateTo("");
    setIdentifierMode("current");
    setIsOpen(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const filters: Record<string, string> = {};
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo)   filters.dateTo   = dateTo;
      // identifierMode is honoured by the records CSV and both Sentral
      // NCCD CSVs (Task #79). Lesson-plan HTML ignores it.
      const supportsIdentifierMode =
        type === "csv" || type === "sentral_nccd_log" || type === "sentral_nccd_summary";
      if (supportsIdentifierMode && identifierMode !== "current") {
        filters.identifierMode = identifierMode;
      }
      await createExport.mutateAsync({ data: { name, type, filters } });
      queryClient.invalidateQueries({ queryKey: getListExportsQueryKey() });
      setIsOpen(false);
      toast({ title: "Export ready", description: "Click Download to get your file." });
    } catch (err) {
      toast({
        title: "Couldn't create the export",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this export?")) return;
    await deleteExport.mutateAsync({ exportId: id });
    queryClient.invalidateQueries({ queryKey: getListExportsQueryKey() });
  };

  // Auth-aware download — API requires Bearer token
  const handleDownload = async (exp: { id: number; downloadUrl?: string | null; name: string; type: string }) => {
    if (!exp.downloadUrl) return;
    setDownloading(exp.id);
    try {
      const token = await getToken();
      const res = await fetch(exp.downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      // For lessons HTML: open in new tab so user can print to PDF
      if (exp.type === "lessons_pdf") {
        const reader = new FileReader();
        reader.onload = () => {
          const htmlWindow = window.open();
          if (htmlWindow) {
            htmlWindow.document.write(reader.result as string);
            htmlWindow.document.close();
          }
        };
        reader.readAsText(blob);
      } else {
        const ext = exp.type === "lessons_pdf" ? ".html" : ".csv";
        a.download = `${exp.name.replace(/[^a-z0-9_\-\s]/gi, "_")}${ext}`;
        a.click();
      }
      URL.revokeObjectURL(url);
    } catch (err) {
      // api-error-helper-exempt: raw fetch (not ApiError); helper still produces a sensible fallback.
      toast({
        title: "Download failed",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    } finally {
      setDownloading(null);
    }
  };

  const selectedTypeDef = EXPORT_TYPES.find((t) => t.value === type) ?? EXPORT_TYPES[0];

  // Task #96 — privacy-friendly preview. Only fetched when:
  //   * the modal is open,
  //   * the export type supports identifier modes, AND
  //   * the teacher chose a reduced mode ("code" or "sequential").
  // The preview shares the same builder pipeline as the actual download
  // (server reuses buildRecordsCsv / buildSentralNccd*Csv), so what
  // teachers see here is exactly what they'll get in the file.
  const previewSupported =
    type === "csv" || type === "sentral_nccd_log" || type === "sentral_nccd_summary";
  const showPreview = isOpen && previewSupported && identifierMode !== "current";
  const previewQuery = usePreviewExport(
    {
      type: type as "csv" | "sentral_nccd_log" | "sentral_nccd_summary",
      identifierMode,
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      limit: 3,
    },
    { query: { enabled: showPreview } },
  );

  // Task #116 — lesson-plan HTML summary preview. Always shown (no
  // identifier-mode gate) because the lessons HTML doesn't have a
  // privacy/identifier toggle — the question teachers most often want
  // to answer is "did I pick the right date range?".
  const showLessonsPreview = isOpen && type === "lessons_pdf";
  const lessonsPreviewParams = {
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  };
  const lessonsPreviewQuery = usePreviewLessonsExport(
    lessonsPreviewParams,
    {
      query: {
        enabled: showLessonsPreview,
        queryKey: ["lessons-preview", lessonsPreviewParams] as const,
      },
    },
  );

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exports</h1>
          <p className="text-sm text-gray-500 mt-0.5">Download records and lesson plans for compliance and reporting</p>
        </div>
        <Button onClick={handleOpen} className="gap-2">
          <Plus className="h-4 w-4" />
          New Export
        </Button>
      </div>

      {/* Export types info */}
      <div className="space-y-4 mb-8">
        {/* Standard exports */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {EXPORT_TYPES.filter((et) => !et.compliance).map((et) => (
            <div key={et.value} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                  <et.icon className="h-5 w-5 text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-gray-900">{et.label}</p>
                    <Badge variant="outline" className="text-xs font-mono">{et.badge}</Badge>
                  </div>
                  <p className="text-xs text-gray-500">{et.description}</p>
                  {et.columns && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {et.columns.slice(0, 5).map((col) => (
                        <span key={col} className="inline-block text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded px-1.5 py-0.5 font-mono">
                          {col}
                        </span>
                      ))}
                      {et.columns.length > 5 && (
                        <span className="inline-block text-xs text-gray-400 px-1.5 py-0.5">+{et.columns.length - 5} more</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Compliance system exports */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-4 w-4 text-teal-600" />
            <span className="text-sm font-semibold text-teal-700">
              {isAuRegion ? "Sentral-formatted NCCD Exports" : `${complianceSystem} Exports`}
            </span>
            <span className="text-xs text-gray-400">
              — designed to make entering data into {isAuRegion ? "Sentral" : "your compliance system"} as fast as possible
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {EXPORT_TYPES.filter((et) => et.compliance).map((et) => (
              <div key={et.value} className="bg-teal-50/40 border border-teal-100 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg flex-shrink-0">
                    <et.icon className="h-5 w-5 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-gray-900">{et.label}</p>
                      <Badge className="text-xs font-mono bg-teal-100 text-teal-700 border-0">{et.badge}</Badge>
                    </div>
                    <p className="text-xs text-gray-500">{et.description}</p>
                    {et.columns && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {et.columns.slice(0, 5).map((col) => (
                          <span key={col} className="inline-block text-xs bg-white border border-teal-100 text-teal-800 rounded px-1.5 py-0.5 font-mono">
                            {col}
                          </span>
                        ))}
                        {et.columns.length > 5 && (
                          <span className="inline-block text-xs text-gray-400 px-1.5 py-0.5">+{et.columns.length - 5} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      )}

      {!isLoading && (!exports || exports.length === 0) && (
        <div className="py-24 text-center border-2 border-dashed border-gray-200 rounded-xl">
          <Download className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No exports yet</h3>
          <p className="text-gray-500 mb-4">Create an export to download your records or lesson plans.</p>
          <Button onClick={handleOpen}>Create First Export</Button>
        </div>
      )}

      <div className="space-y-3">
        {exports?.map((exp) => {
          const typeDef = EXPORT_TYPES.find((t) => t.value === exp.type) ?? EXPORT_TYPES[0];
          const Icon = typeDef.icon;
          const isCompliance = (typeDef as typeof EXPORT_TYPES[0]).compliance;
          return (
            <div key={exp.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 group hover:shadow-sm transition-shadow">
              <div className={`p-2 rounded-lg ${isCompliance ? "bg-teal-50" : "bg-blue-50"}`}>
                <Icon className={`h-6 w-6 ${isCompliance ? "text-teal-600" : "text-blue-500"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{exp.name}</p>
                <p className="text-xs text-gray-500">
                  {(() => {
                    const f = exp.filters as Record<string, string> | null;
                    if (f?.dateFrom || f?.dateTo) {
                      const from = f.dateFrom ? format(new Date(f.dateFrom + "T00:00:00"), "d MMM yyyy") : "start";
                      const to   = f.dateTo   ? format(new Date(f.dateTo   + "T00:00:00"), "d MMM yyyy") : "today";
                      return `${from} – ${to} · `;
                    }
                    return "All dates · ";
                  })()}
                  Created {format(new Date(exp.createdAt), "d MMM yyyy, h:mm a")}
                  {exp.expiresAt && ` · Expires ${format(new Date(exp.expiresAt), "d MMM yyyy")}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs font-mono">{typeDef.badge}</Badge>
                <Badge className="text-xs bg-green-100 text-green-700 border-0">Ready</Badge>
                {exp.downloadUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={downloading === exp.id}
                    onClick={() => handleDownload(exp)}
                  >
                    {exp.type === "lessons_pdf" ? (
                      <ExternalLink className="h-3.5 w-3.5" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    {downloading === exp.id ? "Opening…" : exp.type === "lessons_pdf" ? "Open" : "Download"}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleDelete(exp.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Export</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Export Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  type === "lessons_pdf"          ? "e.g. Term 1 Lesson Plans" :
                  type === "sentral_nccd_log"     ? "e.g. Term 1 NCCD Evidence Log" :
                  type === "sentral_nccd_summary" ? "e.g. 2025 NCCD Annual Summary" :
                  "e.g. Term 1 NCCD Records"
                }
                autoFocus
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Export Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as ExportInputType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPORT_TYPES.map((et) => (
                    <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Date Range <span className="text-gray-400 font-normal">(optional — leave blank to export all)</span></Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <span className="text-xs text-gray-500">From</span>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    max={dateTo || undefined}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-gray-500">To</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    min={dateFrom || undefined}
                  />
                </div>
              </div>
            </div>
            {showLessonsPreview && (
              <div
                className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden"
                data-testid="lessons-preview"
              >
                <div className="px-3 py-1.5 bg-gray-100 border-b border-gray-200">
                  <span className="text-[11px] font-semibold text-gray-700">
                    Preview — what's in the file
                  </span>
                </div>
                <div className="px-3 py-2.5 text-[12px] text-gray-700">
                  {lessonsPreviewQuery.isFetching && !lessonsPreviewQuery.data ? (
                    <span className="text-gray-500">Loading…</span>
                  ) : lessonsPreviewQuery.isError ? (
                    <span className="text-red-600">
                      Couldn't build a preview — try generating the export anyway.
                    </span>
                  ) : lessonsPreviewQuery.data && lessonsPreviewQuery.data.dayCount === 0 ? (
                    <span className="text-gray-500">
                      No lesson plans with content in the selected date range yet.
                    </span>
                  ) : lessonsPreviewQuery.data ? (
                    <>
                      <div data-testid="lessons-preview-counts">
                        <span className="font-semibold">{lessonsPreviewQuery.data.dayCount}</span>{" "}
                        day{lessonsPreviewQuery.data.dayCount === 1 ? "" : "s"},{" "}
                        <span className="font-semibold">{lessonsPreviewQuery.data.lessonCount}</span>{" "}
                        lesson plan{lessonsPreviewQuery.data.lessonCount === 1 ? "" : "s"},{" "}
                        <span className="font-semibold">{lessonsPreviewQuery.data.recordCount}</span>{" "}
                        record{lessonsPreviewQuery.data.recordCount === 1 ? "" : "s"}
                      </div>
                      {lessonsPreviewQuery.data.firstDayLabel && (
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {lessonsPreviewQuery.data.firstDayLabel === lessonsPreviewQuery.data.lastDayLabel
                            ? <>Day: {lessonsPreviewQuery.data.firstDayLabel}</>
                            : <>First day: {lessonsPreviewQuery.data.firstDayLabel} · Last day: {lessonsPreviewQuery.data.lastDayLabel}</>}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
                <p className="px-3 py-1.5 text-[10px] text-gray-500 border-t border-gray-200 bg-white">
                  Built from the same pipeline as the file you'll download.
                </p>
              </div>
            )}
            {(type === "csv" || type === "sentral_nccd_log" || type === "sentral_nccd_summary") && (
              <div className="space-y-1.5">
                <Label>
                  Student identifiers
                </Label>
                <Select value={identifierMode} onValueChange={(v) => setIdentifierMode(v as typeof identifierMode)}>
                  <SelectTrigger data-testid="select-identifier-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Use my default display (recommended)</SelectItem>
                    <SelectItem value="code">Student codes (falls back to initials)</SelectItem>
                    <SelectItem value="sequential">Sequential placeholders (Student 1, Student 2 …)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-gray-500">
                  Reduced identifiers help with data minimisation when sharing the file outside your usual
                  classroom workflow. Surnames and year levels are blanked when a reduced mode is chosen.
                </p>
                {showPreview && (
                  <div
                    className="mt-3 border border-gray-200 rounded-lg bg-gray-50 overflow-hidden"
                    data-testid="export-preview"
                  >
                    <div className="px-3 py-1.5 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-gray-700">
                        Preview — top 3 rows
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {previewQuery.isFetching
                          ? "Loading…"
                          : previewQuery.data
                            ? `${previewQuery.data.totalRows} row${previewQuery.data.totalRows === 1 ? "" : "s"} total`
                            : ""}
                      </span>
                    </div>
                    {/* Task #115 — banner summarising fallback counts across the
                        entire export (not just the previewed rows). Only
                        rendered when at least one student would have fallen
                        back. */}
                    {!previewQuery.isError && previewQuery.data && previewQuery.data.fallbackSummary.affectedStudents > 0 && (
                      <div
                        className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-start gap-2"
                        data-testid="export-preview-fallback-banner"
                      >
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-[11px] text-amber-800 leading-snug">
                          <span className="font-semibold">
                            {previewQuery.data.fallbackSummary.affectedStudents} of {previewQuery.data.fallbackSummary.totalStudents} students
                          </span>{" "}
                          would use a fallback identifier in this export
                          {previewQuery.data.fallbackSummary.studentsWithoutCode > 0 && identifierMode === "code" && (
                            <> — {previewQuery.data.fallbackSummary.studentsWithoutCode} have no student code on file</>
                          )}
                          {previewQuery.data.fallbackSummary.studentsWithoutName > 0 && (
                            <>
                              {previewQuery.data.fallbackSummary.studentsWithoutCode > 0 && identifierMode === "code" ? "; " : " — "}
                              {previewQuery.data.fallbackSummary.studentsWithoutName} have no name recorded
                            </>
                          )}
                          .{" "}
                          {previewQuery.data.fallbackSummary.studentsWithoutCode > 0 ? (
                            <Link
                              href="/app/students?missing-code=true"
                              className="underline font-medium hover:text-amber-900"
                              data-testid="export-preview-fallback-link"
                            >
                              Add codes on the Students page
                            </Link>
                          ) : previewQuery.data.fallbackSummary.studentsWithoutName === 0 && (
                            <Link
                              href="/app/students"
                              className="underline font-medium hover:text-amber-900"
                              data-testid="export-preview-fallback-link"
                            >
                              Review on the Students page
                            </Link>
                          )}
                          {previewQuery.data.fallbackSummary.studentsWithoutName > 0 && (
                            <>
                              {previewQuery.data.fallbackSummary.studentsWithoutCode > 0 ? " · " : ""}
                              <Link
                                href="/app/students?missing-name=true"
                                className="underline font-medium hover:text-amber-900"
                                data-testid="export-preview-fallback-name-link"
                              >
                                Add names on the Students page
                              </Link>
                            </>
                          )}
                          .
                        </div>
                      </div>
                    )}
                    {previewQuery.isError && (
                      <div className="px-3 py-3 text-[11px] text-red-600">
                        Couldn't build a preview — try generating the export anyway.
                      </div>
                    )}
                    {!previewQuery.isError && previewQuery.data && previewQuery.data.rows.length === 0 && (
                      <div className="px-3 py-3 text-[11px] text-gray-500">
                        No matching records in the selected date range yet.
                      </div>
                    )}
                    {!previewQuery.isError && previewQuery.data && previewQuery.data.rows.length > 0 && (
                      <div className="overflow-x-auto max-w-full">
                        <table className="text-[11px] w-full">
                          <thead className="bg-white">
                            <tr>
                              <th className="w-4 px-1 py-1 border-b border-gray-200" aria-label="Fallback flag" />
                              {previewQuery.data.headers.map((h, i) => (
                                <th
                                  key={i}
                                  className="px-2 py-1 text-left font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewQuery.data.rows.map((row, ri) => {
                              const flag = previewQuery.data!.rowFlags[ri] ?? null;
                              const tooltip = flag
                                ? flag.reason === "no_code"
                                  ? `No student code on file${flag.studentLabel ? ` (${flag.studentLabel})` : ""}`
                                  : `Student record has no name${flag.studentLabel ? ` (${flag.studentLabel})` : ""}`
                                : "";
                              return (
                                <tr
                                  key={ri}
                                  className="bg-white even:bg-gray-50"
                                  data-testid={flag ? `export-preview-row-fallback-${flag.reason}` : undefined}
                                >
                                  <td
                                    className="px-1 py-1 align-top border-b border-gray-100 text-center"
                                    title={tooltip}
                                  >
                                    {flag && (
                                      <span
                                        aria-label={tooltip}
                                        className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500"
                                      />
                                    )}
                                  </td>
                                  {row.map((cell, ci) => (
                                    <td
                                      key={ci}
                                      className="px-2 py-1 text-gray-700 border-b border-gray-100 align-top max-w-[14rem] truncate"
                                      title={cell}
                                    >
                                      {cell || <span className="text-gray-300">—</span>}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="px-3 py-1.5 text-[10px] text-gray-500 border-t border-gray-200 bg-white">
                      Built from the same pipeline as the file you'll download.
                    </p>
                  </div>
                )}
              </div>
            )}
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 flex gap-2">
              <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">{selectedTypeDef.hint}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createExport.isPending}>
                {createExport.isPending ? "Generating…" : "Generate Export"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
